/**
 * <ry-carousel>
 *
 * Content slider with autoplay, touch/swipe, dots, arrows, keyboard nav.
 *
 * Usage:
 * <ry-carousel autoplay interval="5000" dots arrows loop>
 *   <div>Slide 1</div>
 *   <div>Slide 2</div>
 * </ry-carousel>
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

export class RyCarousel extends RyElement {
  #currentIndex = 0;
  #slideCount = 0;
  #autoplayTimer: ReturnType<typeof setInterval> | null = null;
  #startX = 0;
  #isDragging = false;

  setup(): void {
    this.#transformMarkup();
    this.#setupAutoplay();

    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    this.on(this, 'keydown', this.#handleKeydown as EventListener);
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="viewport"]')) return;

    const slides = [...this.children];
    this.#slideCount = slides.length;
    if (this.#slideCount === 0) return;

    const showArrows = this.hasAttribute('arrows');
    const showDots = this.hasAttribute('dots');

    // Wrap slides in track
    const track = document.createElement('div');
    track.setAttribute('data-ry-target', 'track');
    track.className = 'ry-carousel__track';
    for (const slide of slides) {
      const wrapper = document.createElement('div');
      wrapper.setAttribute('data-ry-target', 'slide');
      wrapper.className = 'ry-carousel__slide';
      wrapper.appendChild(slide);
      track.appendChild(wrapper);
    }

    // Build viewport
    const viewport = document.createElement('div');
    viewport.setAttribute('data-ry-target', 'viewport');
    viewport.className = 'ry-carousel__viewport';
    viewport.appendChild(track);

    this.innerHTML = '';
    this.appendChild(viewport);

    // Arrows
    if (showArrows) {
      const prev = document.createElement('button');
      prev.setAttribute('data-ry-target', 'prev');
      prev.className = 'ry-carousel__arrow ry-carousel__arrow--prev';
      prev.setAttribute('aria-label', 'Previous slide');
      prev.setAttribute('type', 'button');
      prev.innerHTML = getIcon('chevron-left');
      this.appendChild(prev);
      this.on(prev, 'click', () => this.prev());

      const next = document.createElement('button');
      next.setAttribute('data-ry-target', 'next');
      next.className = 'ry-carousel__arrow ry-carousel__arrow--next';
      next.setAttribute('aria-label', 'Next slide');
      next.setAttribute('type', 'button');
      next.innerHTML = getIcon('chevron-right');
      this.appendChild(next);
      this.on(next, 'click', () => this.next());
    }

    // Dots
    if (showDots) {
      const dotsContainer = document.createElement('div');
      dotsContainer.setAttribute('data-ry-target', 'dots');
      dotsContainer.className = 'ry-carousel__dots';
      for (let i = 0; i < this.#slideCount; i++) {
        const dot = document.createElement('button');
        dot.setAttribute('data-ry-target', 'dot');
        dot.className = 'ry-carousel__dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.setAttribute('type', 'button');
        if (i === 0) dot.setAttribute('data-active', '');
        this.on(dot, 'click', () => this.goTo(i));
        dotsContainer.appendChild(dot);
      }
      this.appendChild(dotsContainer);
    }

    // Touch/swipe
    this.on(viewport, 'pointerdown', this.#handlePointerDown as EventListener);
    this.on(viewport, 'pointermove', this.#handlePointerMove as EventListener);
    this.on(viewport, 'pointerup', this.#handlePointerUp as EventListener);
    this.on(viewport, 'pointercancel', this.#handlePointerUp as EventListener);

    // Pause on hover
    if (this.hasAttribute('pause-on-hover')) {
      this.on(this, 'mouseenter', () => this.#stopAutoplay());
      this.on(this, 'mouseleave', () => this.#setupAutoplay());
    }

    this.goTo(0);
  }

  #handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.next();
    }
  };

  #handlePointerDown = (e: PointerEvent): void => {
    this.#startX = e.clientX;
    this.#isDragging = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  #handlePointerMove = (e: PointerEvent): void => {
    if (!this.#isDragging) return;
    // Prevent scroll while swiping
    e.preventDefault();
  };

  #handlePointerUp = (e: PointerEvent): void => {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    const diff = e.clientX - this.#startX;
    const threshold = 50;
    if (diff > threshold) {
      this.prev();
    } else if (diff < -threshold) {
      this.next();
    }
  };

  #setupAutoplay(): void {
    this.#stopAutoplay();
    if (!this.hasAttribute('autoplay')) return;
    const interval = parseInt(this.getAttribute('interval') ?? '5000', 10);
    this.#autoplayTimer = setInterval(() => this.next(), interval);
  }

  #stopAutoplay(): void {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }

  goTo(index: number): void {
    const loop = this.hasAttribute('loop');
    if (loop) {
      if (index < 0) index = this.#slideCount - 1;
      if (index >= this.#slideCount) index = 0;
    } else {
      if (index < 0 || index >= this.#slideCount) return;
    }

    this.#currentIndex = index;

    const track = this.$<HTMLElement>('[data-ry-target="track"]');
    if (track) {
      track.style.transform = `translateX(-${index * 100}%)`;
    }

    // Update dots
    const dots = this.$$('[data-ry-target="dot"]');
    dots.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('data-active', '');
      } else {
        dot.removeAttribute('data-active');
      }
    });

    this.emit('change', { index, slide: this.#currentIndex });
  }

  next(): void {
    this.goTo(this.#currentIndex + 1);
  }

  prev(): void {
    this.goTo(this.#currentIndex - 1);
  }

  teardown(): void {
    this.#stopAutoplay();
  }
}

customElements.define('ry-carousel', RyCarousel);
